import type { PaginatedResponse, UserDeep } from '@base/types'
import type * as UT from '@base/server/services/user/@types/io/'

export const useUser = defineStore('user', () => {
  const user = useState<UserDeep | undefined>('user', () => undefined)
  const isFetched = useState('user-isFetched', () => false)
  const isFetching = ref(false)

  const { platform } = useAppConfig()

  let stopwatchAuth = () => {}

  const auth = useAuth()
  const { authUser } = storeToRefs(auth)
  const { logout, markUserAsAdmin } = auth

  const verifiedLevelNum = computed(() => user.value?.verifiedLevel?.number ?? 0)
  const isLogged = computed(() => !!user.value)
  const isAdmin = computed(() => user.value?.role === 'ADMIN')

  const fetchUser = async (params: UT.GetUserInput) => {
    if (params.userId)
      return await $superFetch<UT.GetUserOutput>(`/api/admin/users/${params.userId}`, {
        headers: useRequestHeaders(['cookie']),
      })
    else if (params.email)
      return await $superFetch<UT.GetUserOutput>(`/api/admin/users/email/${encodeURIComponent(params.email)}`, {
        headers: useRequestHeaders(['cookie']),
      })
    else
      throw new Error('Invalid parameters for fetching user')
  }

  const fetchUsers = async (params: { search?: string, page: number, pageSize: number, fetchAll?: boolean } = { page: 1, pageSize: 15, fetchAll: false }) => {
    const url = isAdmin.value ? '/api/admin/users' : '/api/users'

    return await $superFetch<PaginatedResponse<UT.GetUsersOutput>>(url, {
      headers: useRequestHeaders(['cookie']),
      query: {
        search: params.search,
        page: params.fetchAll ? undefined : params.page,
        pageSize: params.fetchAll ? undefined : params.pageSize,
      },
    })
  }

  const getCurrentUserData = async (force?: boolean) => {
    isFetching.value = true
    if (!authUser.value)
      throw new Error('Invalid Session')

    if (user.value?.userId === authUser.value.id && !force) {
      isFetching.value = false
      isFetched.value = true
      return
    }

    const result = await $superFetch<UserDeep>('/api/users/data', { headers: useRequestHeaders(['cookie']) })

    isFetching.value = false
    isFetched.value = true
    if (!result) {
      // TODO: handle when user is not exist
      return
    }

    if (result.role === 'ADMIN')
      markUserAsAdmin()

    user.value = result
  }

  const getUserVerifications = async (params: UT.GetUserVerificationsInput) => {
    return await $superFetch<UT.GetUserVerificationsOutput>(`/api/admin/users/verifications`, {
      headers: useRequestHeaders(['cookie']),
      query: params,
    })
  }

  const patchUserVerification = async (params: UT.UpdateUserVerificationInput) => {
    return await $superFetch<UT.UpdateUserVerificationOutput>(`/api/admin/users/verifications/${params.verificationId}`, {
      method: 'PATCH',
      body: params,
      headers: useRequestHeaders(['cookie']),
    })
  }

  const patchUser = async (userDto: UT.UpdateUserInput) => {
    const url = isAdmin.value ? `/api/admin/users/${userDto.userId}` : `/api/users/${userDto.userId}`
    const result = await $superFetch<UT.UpdateUserOutput>(url, {
      method: 'PATCH',
      body: userDto,
      headers: useRequestHeaders(['cookie']),
    })
    await getCurrentUserData(true)

    if (!result)
      return createError('User not found')
  }

  stopwatchAuth()
  stopwatchAuth = watch(authUser, async () => {
    if (authUser.value)
      return await getCurrentUserData()

    user.value = undefined
  }, {
    immediate: true,
  })

  const waitUntilResolve = async () => {
    if (authUser.value)
      await until(isFetched).toBe(true, { timeout: 3000 })
  }

  const dispose = (stopwatchAuth)

  return {
    user,
    isFetched,
    isFetching,
    waitUntilResolve,
    patchUser,
    getCurrentUserData,
    getUserVerifications,
    patchUserVerification,
    dispose,
    logout,
    fetchUsers,
    fetchUser,
    isLogged,
    isInspector: computed(() => user.value?.role === 'ADMIN' || (user.value?.role === 'INSPECTOR' && user.value?.platformId === platform.id)),
    isAdmin,
    isExchangeOfficeOwner: computed(() => user.value?.role === 'EXCHANGE_OFFICE_OWNER'),
    isExchangeOfficeOwnerVerified: computed(() => user.value?.verifiedLevelId === 'EXCHANGE_OFFICE_OWNER' && user.value?.role === 'EXCHANGE_OFFICE_OWNER'),
    isExchangeOfficeEmployee: computed(() => (user.value?.role === 'USER' && user.value?.exchangeOfficeId !== null) || user.value?.role === 'EXCHANGE_OFFICE_OWNER'),
    verifiedLevelNum,
    limitRemaining: computed(() => (user.value?.verifiedLimit ?? 0) - (user.value?.transactionSum ?? 0)),
    identityVerification: computed(() => user.value?.userVerifications?.find(v => v.type === 'IDENTITY')),
    incomeVerification: computed(() => user.value?.userVerifications?.find(v => v.type === 'INCOME')),
    hasLockedName: computed(() => (!user.value?.isCompany && verifiedLevelNum.value >= 1) || verifiedLevelNum.value >= 2),
    hasLockedCompanyName: computed(() => user.value?.isCompany && verifiedLevelNum.value >= 1),
    hasLockedCompanyIdNumber: computed(() => user.value?.isCompany && verifiedLevelNum.value >= 1),
    referralLink: computed(() => `${useRequestURL().origin}/invite/${user.value?.userId}`),
    rewardPercentExchange: computed(() => user.value?.platform?.referralRewards?.find(r => r.rewardType === 'EXCHANGE')?.rewardPercent ?? 0),
    rewardEurKyc: computed(() => user.value?.platform?.referralRewards?.find(r => r.rewardType === 'KYC')?.rewardEur ?? 0),
    rewardEurKyb: computed(() => user.value?.platform?.referralRewards?.find(r => r.rewardType === 'KYB')?.rewardEur ?? 0),
  }
})
